import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, useLocation } from "@tanstack/react-router";
import { HttpStatusCode } from "axios";
import { z } from "zod";

import { AuthenticationScreenContainer, errorToast } from "@/components";
import { HelpModal } from "@/domains/profile/components/help-modal";
import { loginUrlSchema } from "@/domains/profile/models/schemas";
import { env } from "@/env";
import { useAuth } from "@/hooks";
import { Button, Divider, EmailInput, focusRing, Heading, PasswordInput } from "@/ui";
import { validateError } from "@/utils/validation";
import { PersonifyButton } from "./-components";

const schema = z.object({
  email: z.email({ message: "Invalid email address" }),
  password: z.string().min(1, { message: "Please enter a password" }),
});

type LoginFormData = z.infer<typeof schema>;

const Login = () => {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  const { mutate: requestResetPassword } = useAuth().useRequestResetPassword();
  const { mutate: login } = useAuth().useLogin();
  const location = useLocation();
  const { animate } = (location.state ?? {}) as { animate?: boolean };
  const [openModal, setOpenModal] = useState(false);

  const {
    formState: { errors },
    handleSubmit,
    register,
    resetField,
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(schema),
  });

  const handleLogin = (data: LoginFormData) => {
    login(data, {
      onSuccess: () => {
        void navigate({ to: search.redirect ?? "/" });
      },
      onError: (err) => {
        const error = validateError(err);

        if (error?.response.data.status === HttpStatusCode.Unauthorized) {
          setError("password", {
            message: "Incorrect password, please try again",
          });
          setError("root.loginFailed", { type: `${HttpStatusCode.Unauthorized}` });

          return;
        }

        if (
          error?.response.data.status === HttpStatusCode.UnprocessableEntity &&
          error.response.data.error.code === "password_hashing_exception"
        ) {
          navigate({
            to: "/password-expired",
          });
          requestResetPassword({ email: data.email });

          return;
        }

        errorToast(err);
      },
    });
  };

  return (
    <>
      <HelpModal
        closeModal={() => {
          return setOpenModal(false);
        }}
        isOpen={openModal}
      />
      <AuthenticationScreenContainer
        animate={animate ?? true}
        footer={
          <>
            <p className="text-center">
              New to EO Care Plans?{" "}
              <Link
                className={`rounded-sm font-semibold text-content underline decoration-accent decoration-2 transition-colors hover:text-accent ${focusRing}`}
                to="/profiling"
              >
                Get started here
              </Link>
            </p>

            <p className="flex items-center gap-3 font-semibold">
              <a
                className={`rounded-sm underline-offset-4 transition-colors hover:text-accent hover:underline ${focusRing}`}
                href={`${env.VITE_MARKETING_SITE_DOMAIN}/web/frequently-asked-questions`}
                rel="noreferrer"
                target="_blank"
              >
                FAQ & Help
              </a>

              <span aria-hidden="true" className="text-content-subtle">
                &bull;
              </span>

              <Button
                className={`rounded-sm border-0 p-0 font-semibold underline-offset-4 transition-colors hover:text-accent hover:underline ${focusRing}`}
                onClick={() => {
                  return setOpenModal(true);
                }}
                size="tight"
                variant="focus"
              >
                Contact us
              </Button>
            </p>

            <p className="max-w-sm text-center text-sm text-balance text-content-muted">
              Please consult with your doctor before beginning any cannabinoid treatments.
            </p>
          </>
        }
      >
        <div className="flex flex-col gap-8">
          <header className="flex flex-col items-center gap-2 text-center">
            <Heading className="font-extrabold" level={1}>
              Welcome
            </Heading>
            <p className="text-content-secondary">Log in to your EO Care Plan</p>
          </header>

          <form className="flex flex-col gap-5" onSubmit={handleSubmit(handleLogin)}>
            <EmailInput
              autoComplete="email"
              className="h-14"
              error={errors.email?.message}
              id="email"
              label="Email"
              placeholder="you@example.com"
              reset={() => {
                return resetField("email");
              }}
              type="email"
              {...register("email")}
              autoFocus
            />

            <div className="flex flex-col gap-2">
              <PasswordInput
                autoComplete="current-password"
                className="h-14"
                error={errors.password?.message}
                id="password"
                label="Password"
                placeholder="Enter your password"
                showDisplayPasswordButton
                {...register("password")}
              />

              <Link
                className={`self-end rounded-sm px-1 py-1 font-semibold text-accent underline-offset-4 transition-colors hover:text-accent-hover hover:underline ${focusRing}`}
                to="/forgot-password"
              >
                Forgot password?
              </Link>
            </div>

            <Button className="mt-2 w-full" size="lg" type="submit">
              Log in
            </Button>
          </form>

          {env.VITE_PERSONIFY_ENABLED === "true" && (
            <div className="flex flex-col gap-6">
              <Divider.Root>
                <Divider.Line />
                <Divider.Label>OR</Divider.Label>
                <Divider.Line />
              </Divider.Root>

              <PersonifyButton>Sign in with Personify Health</PersonifyButton>
            </div>
          )}
        </div>
      </AuthenticationScreenContainer>
    </>
  );
};

export const Route = createFileRoute("/_guest/login/")({
  component: Login,
  validateSearch: loginUrlSchema,
});
